/* oxlint-disable typescript/no-unused-vars */
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Microfrontend, Provider as MicrofrontendProvider } from '@cian/mf-react';
import { ICianMFUIElement } from '@cian/mf-registry/browser';

import { ERuntimeSettings } from 'shared/common/types/runtimeSettings';
import { IWidgetManifest } from 'shared/common/types/widgetManifest';
import { useApplicationContext } from 'shared/common/utils/applicationContext';

import { useTracking } from 'shared/mortgageMiniCalculator/utils/tracking/useTracking';
import { EAuthAnalyticsEvents, ETelemetryEventType } from '../../constants';
import { useCurrentSelector } from '../../hooks/useCurrentSelector';
import { clearSaveOfferResult, closeAuthorizationModal, closeModal } from '../../redux';
import {
  selectDefaultRedirectUrl,
  selectProgramsVersion,
  selectRealEstateType,
  selectRedirectAuthorizationModal,
} from '../../selectors/calculator';
import { selectSaveOfferResult } from '../../selectors/calculator/selectSaveOfferResult';
import { selectIsOpenAuthorizationModal } from '../../selectors/modal';
import { redirect } from '../../utils';

export const LoginWidgetMicrofrontend = () => {
  const dispatch = useDispatch();
  const defaultRedirectUrl = useSelector(selectDefaultRedirectUrl);
  const redirectUrl = useSelector(selectRedirectAuthorizationModal);
  const realEstateType = useSelector(selectRealEstateType);
  const programsVersion = useSelector(selectProgramsVersion);
  const mfHostRef = useRef<ICianMFUIElement | null>(null);
  const handlersRef = useRef({
    handleUserAuth: () => {},
    handleCloseWidget: () => {},
    handleAuthWidgetAnalytics: (_e: CustomEvent<EAuthAnalyticsEvents>) => {},
  });
  const { config, logger, telemetry, microfrontendsRegistry } = useApplicationContext();
  const isEnableGetOffersV3 = config.getStrict(ERuntimeSettings.enableGetOffersV3);

  const isAuthorizedModalOpen = useSelector(selectIsOpenAuthorizationModal);
  const getCurrentSaveOfferResult = useCurrentSelector(selectSaveOfferResult);

  const {
    trackOpenPhoneAuthModal,
    trackOpenSmsAuthModal,
    trackClickOnRepeatSms,
    trackClickConfirmPhone,
    trackClickConfirmSms,
    trackAgreeToCommercial,
    trackAuthorizationSuccess,
  } = useTracking();

  const shouldUseAuthActionV3 = Boolean(isEnableGetOffersV3 && programsVersion && realEstateType);

  const handleCloseWidget = () => {
    dispatch(closeAuthorizationModal());
  };

  const handleUserAuthV3 = () => {
    trackAuthorizationSuccess();
    telemetry.increment(ETelemetryEventType.AuthSuccess);

    // Получаем актуальное значение из стора через наш красивый хук
    const currentSaveOfferResult = getCurrentSaveOfferResult();

    if (currentSaveOfferResult?.redirectUrl) {
      const targetUrl = currentSaveOfferResult.redirectUrl;
      dispatch(clearSaveOfferResult());
      redirect(targetUrl);
    } else {
      logger.error('В результате сохранения офферов нет redirectUrl', {
        domain: 'LoginWidgetMicrofrontend',
      });
      dispatch(closeAuthorizationModal());
      dispatch(closeModal());
    }
  };

  const handleUserAuthOld = () => {
    trackAuthorizationSuccess();
    telemetry.increment(ETelemetryEventType.AuthSuccess);

    const targetUrl = redirectUrl || defaultRedirectUrl;
    redirect(targetUrl);
  };

  const handleUserAuth = shouldUseAuthActionV3 ? handleUserAuthV3 : handleUserAuthOld;

  const handleAuthWidgetAnalytics = (e: CustomEvent<EAuthAnalyticsEvents>) => {
    switch (e.detail) {
      case EAuthAnalyticsEvents.PhoneScreenShown:
        telemetry.increment(ETelemetryEventType.PhoneScreenShown);
        trackOpenPhoneAuthModal();
        break;
      case EAuthAnalyticsEvents.PhoneScreenConfirm:
        trackClickConfirmPhone();
        break;
      case EAuthAnalyticsEvents.CodeScreenShown:
        telemetry.increment(ETelemetryEventType.CodeScreenShown);
        trackOpenSmsAuthModal();
        break;
      case EAuthAnalyticsEvents.CodeScreenConfirm:
        trackClickConfirmSms();
        break;
      case EAuthAnalyticsEvents.CodeScreenResend:
        trackClickOnRepeatSms();
        break;
      case EAuthAnalyticsEvents.AdvertisingConsent:
        trackAgreeToCommercial();
        break;
    }
  };

  handlersRef.current.handleUserAuth = handleUserAuth;
  handlersRef.current.handleCloseWidget = handleCloseWidget;
  handlersRef.current.handleAuthWidgetAnalytics = handleAuthWidgetAnalytics;

  const onUserAuthEvent = useCallback(() => {
    void handlersRef.current.handleUserAuth();
  }, []);

  const onCloseEvent = useCallback(() => {
    handlersRef.current.handleCloseWidget();
  }, []);

  const onAnalyticsEvent = useCallback((e: Event) => {
    handlersRef.current.handleAuthWidgetAnalytics(e as CustomEvent<EAuthAnalyticsEvents>);
  }, []);

  const setMicrofrontendHostRef = useCallback(
    (node: ICianMFUIElement | null) => {
      const prev = mfHostRef.current;
      if (prev) {
        prev.removeEventListener('user-auth', onUserAuthEvent);
        prev.removeEventListener('close', onCloseEvent);
      }
      mfHostRef.current = node;
      if (node) {
        node.addEventListener('user-auth', onUserAuthEvent);
        node.addEventListener('close', onCloseEvent);
      }
    },
    [onUserAuthEvent, onCloseEvent],
  );

  useEffect(() => {
    document.addEventListener('analytics', onAnalyticsEvent);

    return () => document.removeEventListener('analytics', onAnalyticsEvent);
  }, [onAnalyticsEvent]);

  const manifest = config.get<IWidgetManifest>(ERuntimeSettings.loginWidgetManifest);

  if (!manifest) {
    logger.error('Отсутствует манифест виджета авторизации', {
      domain: 'microfrontends.login-widget-b2c.manifest',
    });

    return null;
  }

  return (
    <MicrofrontendProvider registry={microfrontendsRegistry}>
      <Microfrontend ref={setMicrofrontendHostRef} {...manifest} parameters={{ isWidgetOpen: isAuthorizedModalOpen }} />
    </MicrofrontendProvider>
  );
};
